// THE definition of "what is a workspace file", and of the names one may be
// given. Every surface that mints, filters, warms or validates a path answers
// from here: the nav's `+` menu, Studio's New-file dialog, Studio's `?new=`
// hand-off, the VFS seeder, and the git cache's supported-type gate.
//
// It lives in viewer-core because that is the lowest package all of them
// already depend on — the same reason `isMarkdownPath` lives here (vfs.ts).
// It had been copied into four of those surfaces instead, on the belief that
// separate packages could not share it, and the copies drifted the way copies
// do: an ASCII-only charset gate in one refused `платежи/package.archspace`
// while the other three accepted, seeded and indexed it; a byte-exact `.md`
// test in the seeder dropped a `README.MD` the dialog had just created.
//
// Two laws underpin all of it:
//   * CASING. `.arch` and `package.archspace` are matched BYTE-EXACTLY by every
//     loader (engine `collectArchPaths`, `discover-roots`, the server tree
//     walk); markdown is case-INSENSITIVE (`isMarkdownPath`). So `X.ARCH` is an
//     inert file that merely looks like a source, while `NOTES.MD` is a real
//     document. Typed names are canonicalized (below) rather than refused.
//   * SHAPE. A typed name becomes git tree-entry names verbatim, so `..`
//     (`hasDotdot`) and empty segments ("a//b") produce a commit real git
//     refuses to clone — written by the product whose job is serving clones.

import { parsePackageManifest } from "@archlang/engine";
import { isMarkdownPath } from "./vfs.js";

export const ARCH_EXT = ".arch";
export const ARCHSPACE_EXT = ".archspace";
export const MD_EXT = ".md";
/** The one filename the toolchain reads as a manifest. */
export const PACKAGE_MANIFEST = `package${ARCHSPACE_EXT}`;

export { isMarkdownPath };

/** Bare basename of a path — no leading-slash or separator assumptions. */
export function basenameOf(path: string): string {
  const bare = path.replace(/^\/+/, "");
  return bare.slice(bare.lastIndexOf("/") + 1);
}

/** Directory of a path, "" at the root. */
export function dirOf(path: string): string {
  const bare = path.replace(/^\/+/, "");
  return bare.includes("/") ? bare.slice(0, bare.lastIndexOf("/")) : "";
}

/**
 * Is `path` a file the toolchain READS? `.arch` sources, the
 * `package.archspace` manifest (that exact basename — any other `.archspace`
 * stem is a file nothing loads), and `.md`/`.markdown` documents.
 *
 * Everything else in a workspace — `archlang.json`, widget scripts, `LICENSE`
 * — is a plain file the nav lists and no loader parses.
 */
export function isWorkspaceFile(path: string): boolean {
  const base = basenameOf(path);
  return base === PACKAGE_MANIFEST || base.endsWith(ARCH_EXT) || isMarkdownPath(base);
}

/** Does the typed name already carry a workspace extension of its own? Such a
 *  name WINS over a menu's default ext — picking "Architecture model" and
 *  typing `package.archspace` must not mint `package.archspace.arch`.
 *
 *  Casing-insensitive, because a user typing `PACKAGE.ARCHSPACE` clearly means
 *  the extension — {@link canonicalBasename} then fixes the spelling, so the
 *  name that reaches the workspace is one the (byte-exact) engine loads. */
export function hasWorkspaceExt(name: string): boolean {
  return /\.(arch|archspace|md|markdown)$/i.test(name);
}

/**
 * Canonical casing for a typed basename.
 *
 * `package.ARCHSPACE` was the worst case: the extension check owned it while
 * `isManifestPath` disowned it, so it landed unseeded — i.e. as a manifest that
 * is a parse error on sight. The extension has exactly one legal spelling; type
 * any casing, get that one. Markdown is left alone — case-insensitivity there
 * is the actual law, so `NOTES.MD` is a real document.
 */
export function canonicalBasename(base: string): string {
  const dot = base.lastIndexOf(".");
  if (dot <= 0) return base;
  const ext = base.slice(dot).toLowerCase();
  if (ext !== ARCH_EXT && ext !== ARCHSPACE_EXT) return base;
  const stem = base.slice(0, dot);
  return ext === ARCHSPACE_EXT && stem.toLowerCase() === "package"
    ? PACKAGE_MANIFEST // the manifest has ONE legal name, stem included
    : stem + ext;
}

/** Canonical spelling of a whole typed path — only the basename can change.
 *  Backslashes fold to "/" first: a pasted Windows path would otherwise mint
 *  one file whose NAME contains a backslash (git accepts that silently). */
export function canonicalPath(path: string): string {
  const segs = path.replace(/\\/g, "/").split("/");
  segs[segs.length - 1] = canonicalBasename(segs[segs.length - 1]!);
  return segs.join("/");
}

/** A leading dot is part of the NAME (".gitignore"), but a name that is ONLY an
 *  extension (".arch", "....arch") has no stem to be a module — it commits fine
 *  and then behaves as no module at all. */
export function hasStem(base: string): boolean {
  return !hasWorkspaceExt(base) || !/^\.*$/.test(base.slice(0, base.lastIndexOf(".")));
}

/** Can this be one segment of a workspace path? Rejects `..` (a traversal, and
 *  in Studio a git tree entry real git refuses), `.`, empty ("a//b", a leading
 *  "/"), and a pasted drive letter (which would mint a dir named "C:"). */
export function isLegalSegment(seg: string): boolean {
  return seg !== "" && seg !== "." && seg !== ".." && !/^[A-Za-z]:$/.test(seg);
}

/** Is `path` (bare or leading-slash) the manifest of some directory? */
export function isManifestPath(path: string): boolean {
  return basenameOf(path) === PACKAGE_MANIFEST;
}

/**
 * Is a typed path one the workspace can hold AND one the toolchain reads?
 * The curated gate: shape first, then the known-type set.
 *
 * Charset is deliberately NOT restricted — the language takes unicode
 * identifiers, and an ASCII-only gate refused names every other surface
 * accepted, seeded and indexed.
 */
export function isWorkspacePath(path: string): boolean {
  const segs = path.replace(/\\/g, "/").split("/");
  if (!segs.every(isLegalSegment)) return false;
  const base = canonicalBasename(segs[segs.length - 1]!);
  return isWorkspaceFile(base) && hasStem(base);
}

/**
 * Resolve a typed name against its directory into a bare workspace path, or
 * `null` when the name is not one the workspace can hold.
 *
 * The name box accepts a PATH ("sub/thing.arch"), so the typed part gets the
 * segment checks a path gets anywhere else. A LEADING "/" means "from the
 * workspace root" — the natural reading, and it kept the nav's own lead from
 * doubling into "//LICENSE". That decision is made on what was TYPED, before
 * "\" folds into "/": a name merely STARTING with a backslash is a name, and
 * reading it as root-anchored moved the file out of the directory the user
 * typed it in — worst as "\package.archspace", which declared a root package
 * nobody asked for.
 *
 * Unlike {@link isWorkspacePath} this accepts ANY basename: the nav's free-form
 * "Other file…" mints plain files (`LICENSE`, `archlang.json`) too.
 */
export function resolveNewPath(dir: string, name: string): string | null {
  // `dir` comes from the tree itself and is trusted; only the TYPED name is
  // adversarial input.
  const fromRoot = name.startsWith("/");
  const slashed = (fromRoot ? name.replace(/^\/+/, "") : name)
    .replace(/\\/g, "/")
    .replace(/^\/+/, ""); // any leading "/" left here came from a backslash
  const typed = slashed.split("/");
  if (!typed.every(isLegalSegment)) return null;
  const out = [...(fromRoot ? [] : dir.split("/").filter(Boolean)), ...typed];
  if (out.length === 0) return null;
  const base = canonicalBasename(out[out.length - 1]!);
  if (!hasStem(base)) return null;
  out[out.length - 1] = base;
  return out.join("/");
}

/** Does a manifest exist STRICTLY above `dir` — i.e. is `dir` already inside a
 *  package? Then a manifest created there is a SPACE (a visibility boundary
 *  inside that package), not a second package root. */
export function hasAncestorManifest(dir: string, paths: Iterable<string>): boolean {
  for (const p of paths) {
    if (!isManifestPath(p)) continue;
    const owner = dirOf(p);
    if (owner !== dir && (owner === "" || dir === owner || dir.startsWith(owner + "/"))) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Seeding. A manifest cannot be created empty: the parser requires exactly one
// of `package:` or `name:`, so a blank `.archspace` is a parse error on sight.
// ---------------------------------------------------------------------------

/** One identifier-ish segment of a seeded name, or "" when nothing is left.
 *
 *  Keeps letters (any script — the language accepts unicode identifiers),
 *  digits and inner underscores. A LEADING underscore moves to the end rather
 *  than being dropped: `_` is illegal as an identifier start (the lexer stops
 *  with "Unexpected character: _"), but dropping it made `_internal` and
 *  `internal` seed the byte-identical name, so two sibling packages were born
 *  with one identity. */
function identSegment(segment: string): string {
  // Filtered code point by code point, and ASTRAL letters are dropped: `\p{L}`
  // matches `𝔸`/`𝕏`, but the lexer scans UTF-16 code units and stops with
  // "Unexpected character: \ud835". Keeping them made every candidate fail, so
  // `𝔸lpha/` and `𝔹eta/` both fell through to the fallback and two sibling
  // packages were born with one identity — the collision the underscore rule
  // above exists to prevent, through a different door.
  const kept = [...segment].filter((ch) => ch.length === 1 && /[\p{L}\p{N}_]/u.test(ch)).join("");
  const lead = kept.match(/^_+/)?.[0] ?? "";
  return lead ? kept.slice(lead.length) + lead : kept;
}

/** Short stable tag for a dir path, so two dirs that BOTH resolve to nothing
 *  (`---/`, `𝕏/`) don't seed the same name — and so neither one silently
 *  duplicates a dir genuinely named `workspace`/`space`. FNV-1a, base36. */
function pathTag(dir: string): string {
  let h = 0x811c9dc5;
  for (const ch of dir) h = Math.imul(h ^ ch.codePointAt(0)!, 0x01000193) >>> 0;
  return h.toString(36).slice(0, 4);
}

/** Title-case the first letter ONLY when doing so is a 1:1 swap. JS
 *  `toUpperCase` is neither length- nor letter-preserving: "ß"→"SS", "ﬁ"→"FI",
 *  and the dotless "ı"→"I" is a different letter — each one corrupts the name
 *  the user is about to read, and all of them parse, so nothing catches it. */
function capitalize(word: string): string {
  const first = [...word][0]!;
  const upper = first.toUpperCase();
  // Round-trip test, not a length test: "ß"→"SS" is caught by length, but the
  // dotless "ı"→"I" is one code point and STILL a different letter (it lowers
  // back to "i").
  const faithful = [...upper].length === 1 && upper.toLowerCase() === first;
  return faithful ? upper + word.slice(first.length) : word;
}

/** Make `name` something the manifest grammar actually accepts, asking the REAL
 *  parser rather than a hand-kept list.
 *
 *  Two classes are otherwise legal-looking and fatal: a digits-only segment
 *  lexes as a NUMBER (`999`, and `api/v1/2` — an ordinary versioned dir), and a
 *  reserved word lexes as a keyword (`use`, `type`, `view`, `on`, `in`, `out`,
 *  `as`, `from`, `if`, `else`, `process`, `aspect`, `true`, `false` — too long
 *  and too volatile a list to hardcode). A trailing "_" fixes both and stays a
 *  legal identifier; the parser is the authority on whether it worked. */
function acceptedByParser(field: "package" | "name", value: string): string | null {
  if (value === "") return null;
  for (const candidate of [value, `${value}_`, `x${value}`]) {
    if (parsePackageManifest(`${field}: ${candidate}\n`).errors.length === 0) return candidate;
  }
  return null; // unusable — the CALLER decides, so a give-up word never
  // lands mid-path ("acme.workspace.shop" named nothing anybody typed).
}

/** Dotted package name for a bare dir path — "src/acme/shop" → "src.acme.shop". */
function dottedName(dir: string): string {
  const parts = dir
    .split("/")
    .map((seg) => acceptedByParser("package", identSegment(seg).toLowerCase()))
    .filter((seg): seg is string => seg !== null);
  if (parts.length) return parts.join(".");
  return dir ? `workspace_${pathTag(dir)}` : "workspace";
}

/** Space name for a bare dir path — "src/loans" → "Loans". */
function spaceName(dir: string): string {
  const clean = identSegment(dir.split("/").filter(Boolean).pop() ?? "");
  const named = clean === "" ? null : acceptedByParser("name", capitalize(clean));
  return named ?? (dir ? `Space_${pathTag(dir)}` : "Space");
}

/**
 * The content a new manifest is born with.
 *
 * A manifest under a directory whose ANCESTOR already declares a package is a
 * SPACE, so it gets `name:`; one with no package above it is a package root and
 * gets `package:`. Both forms are the minimal valid manifest — the user renames
 * from there.
 */
export function manifestSeed(dir: string, insidePackage: boolean): string {
  return insidePackage ? `name: ${spaceName(dir)}\n` : `package: ${dottedName(dir)}\n`;
}

/** Content a newly created `path` is born with, given the paths that already
 *  exist — "" for everything but a manifest. The path-shaped entry point, for
 *  callers that create from a PATH rather than from a menu choice. */
export function newFileSeed(path: string, existing: Iterable<string>): string {
  if (!isManifestPath(path)) return "";
  const dir = dirOf(path);
  return manifestSeed(dir, hasAncestorManifest(dir, existing));
}
